// ============================================================================
// IWorkflowPermissionResolver — consumer-supplied permission seam
// ============================================================================
// The framework cannot reference recsuit's role engine directly (soft isolation
// rule, see README). Instead, consumers provide a resolver via React context.
// The framework calls .hasPermission(code) / .hasRole(code) when it needs to
// gate a UI element (e.g. greying out an action-bar button the user can't fire).
//
// Recsuit wires this in App.tsx:
//
//   <WorkflowPermissionProvider resolver={recsuitWorkflowPermissionAdapter}>
//     ...
//   </WorkflowPermissionProvider>
//
// At extraction time, every consumer follows the same pattern.

import React, { createContext, useContext, type ReactNode } from 'react';

export interface IWorkflowPermissionResolver {
  /** True if the current user has the named permission code. */
  hasPermission: (code: string) => boolean;
  /** True if the current user holds the named role. */
  hasRole: (roleCode: string) => boolean;
  /** All role codes held by the current user — used by the inbox to filter. */
  roleCodes: () => string[];
}

const NoopResolver: IWorkflowPermissionResolver = {
  // Default: permissive. The action-bar/inbox renders unguarded. The server
  // is the real authority via [HasPermission] + role-code checks. This default
  // exists so the framework boots without a consumer wired in (smoke tests etc.).
  hasPermission: () => true,
  hasRole: () => true,
  roleCodes: () => [],
};

const WorkflowPermissionCtx = createContext<IWorkflowPermissionResolver>(NoopResolver);

export const WorkflowPermissionProvider: React.FC<{
  resolver: IWorkflowPermissionResolver;
  children: ReactNode;
}> = ({ resolver, children }) => (
  <WorkflowPermissionCtx.Provider value={resolver}>
    {children}
  </WorkflowPermissionCtx.Provider>
);

export const useWorkflowPermissions = (): IWorkflowPermissionResolver =>
  useContext(WorkflowPermissionCtx);
